import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axiosInstance from '../../services/AxiosInstance';
import { formatDateTimeToDDMMMYYYY, formatDateToDDMMMYYYY } from '../../utils/dateFormatter';
import CommonTable from '../components/CommonTable/CommonTable';
import './taskdetails.css';
import '../Documents/commonstyle.css';

const PAGE_SIZE = 50;

const TaskDailyUpdatesList = () => {
  const { taskid, projectId } = useParams();
  const navigate = useNavigate();
  const isProjectMode = !!projectId;
  const entityId = isProjectMode ? projectId : taskid;
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [entityTitle, setEntityTitle] = useState('');
  const [projectInfo, setProjectInfo] = useState({ name: '', id: null, start_date: null, due_date: null });

  const fetchUpdates = async (reset = false, loadMoreOffset = 0) => {
    const currentOffset = reset ? 0 : loadMoreOffset;
    try {
      setLoading(true);
      if (isProjectMode) {
        const params = new URLSearchParams({
          limit: String(PAGE_SIZE),
          offset: String(currentOffset),
        });
        const response = await axiosInstance.get(`/project/daily-updates/${projectId}?${params.toString()}`);
        const data = response.data?.data || response.data || [];
        const list = Array.isArray(data) ? data : (data.updates || data.list || []);
        if (reset) {
          setUpdates(list);
        } else {
          setUpdates((prev) => [...prev, ...list]);
        }
        setHasMore(list.length === PAGE_SIZE);
        // Fallback: use project name from first update's Project association if entityTitle not set yet
        const projectFromUpdate = list[0]?.Project || list[0]?.project;
        if (list.length > 0 && projectFromUpdate?.name) {
          setEntityTitle((prev) => (prev && !prev.startsWith('Project #') ? prev : projectFromUpdate.name));
        }
      } else {
        // Daily updates page: always list ALL transaction statements regardless of user
        const params = new URLSearchParams({
          task_id: taskid,
          limit: PAGE_SIZE,
          offset: String(currentOffset),
        });
        const response = await axiosInstance.get(`/task/daily-updates?${params.toString()}`);
        const data = response.data?.data || [];
        if (reset) {
          setUpdates(data);
        } else {
          setUpdates((prev) => [...prev, ...data]);
        }
        setHasMore(data.length === PAGE_SIZE);
      }
    } catch (err) {
      console.error('Failed to fetch daily updates:', err);
      setUpdates([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEntityDetails = async () => {
    try {
      if (isProjectMode) {
        const response = await axiosInstance.get(`/project/details/${projectId}`);
        const resData = response.data;
        // Backend returns { success: true, data: project }; projectdetails also handles { data: { data: project } }
        const project = resData?.data?.data || resData?.data || resData?.project || resData;
        const projectName = project?.name || project?.Project?.name || '';
        setEntityTitle(projectName || `Project #${projectId}`);
        setProjectInfo({
          name: projectName,
          id: project?.id || projectId,
          start_date: project?.start_date || project?.startDate || null,
          due_date: project?.end_date || project?.due_date || project?.dueDate || null,
        });
      } else {
        const response = await axiosInstance.get(`/task/taskdetails/${taskid}`);
        const data = response.data;
        setEntityTitle(data?.title || `Task #${taskid}`);
        setProjectInfo({
          name: data?.project_name || data?.project?.name || '',
          id: data?.project_id || data?.project?.id || null,
          start_date: data?.start_date || null,
          due_date: data?.due_date || null,
        });
      }
    } catch {
      setEntityTitle(isProjectMode ? `Project #${projectId}` : `Task #${taskid}`);
    }
  };

  useEffect(() => {
    if (entityId) {
      fetchEntityDetails();
      fetchUpdates(true);
    }
  }, [entityId, isProjectMode]);

  const loadMore = () => {
    if (!loading && hasMore && updates.length > 0) {
      fetchUpdates(false, updates.length);
    }
  };

  const handleExportDailyUpdates = async () => {
    try {
      const url = isProjectMode
        ? `/project/daily-updates/${projectId}/export-xlsx`
        : `/task/daily-updates/export-xlsx?task_id=${taskid}`;
      const res = await axiosInstance.get(url, { responseType: 'blob' });
      const contentType = res.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        const text = await res.data.text();
        const data = JSON.parse(text);
        if (data?.success === false) {
          toast.error(data.message || 'No data to export');
          return;
        }
      }
      const downloadUrl = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = downloadUrl;
      const name = (entityTitle || (isProjectMode ? 'Project' : 'Task')).replace(/[^a-zA-Z0-9_-]/g, '_');
      a.download = `${isProjectMode ? 'Project' : 'Task'}_DailyUpdates_${name}_${entityId}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Daily updates exported successfully');
    } catch (err) {
      console.error('Export error:', err);
      const errMsg = err.response?.data instanceof Blob
        ? 'Export failed'
        : (err.response?.data?.message || err.message || 'Failed to export daily updates');
      toast.error(errMsg);
    }
  };

  const columns = [
    {
      key: 'index',
      header: '#',
      render: (row, rowIndex) => rowIndex + 1,
      headerStyle: { width: '50px' },
      cellStyle: { whiteSpace: 'nowrap' },
    },
    ...(isProjectMode
      ? [
          {
            key: 'task',
            header: 'Task',
            accessor: (row) => row.Task?.title || row.task_title || '',
            headerStyle: { width: '12rem' },
            cellStyle: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '12rem' },
          },
        ]
      : []),
    {
      key: 'date',
      header: 'Date',
      render: (row) => formatDateTimeToDDMMMYYYY(row.updated_at),
      headerStyle: { width: '9.5rem' },
      cellStyle: { whiteSpace: 'nowrap' },
    },
    {
      key: 'updatedBy',
      header: 'Updated By',
      accessor: (row) => row.updatedByUser?.full_name || 'Unknown',
      headerStyle: { width: '10rem' },
      cellStyle: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '10rem' },
    },
    {
      key: 'update',
      header: 'Update',
      render: (row) => {
        const val = row.update ?? row.dataValues?.update;
        const text = typeof val === 'string' ? val : (val != null ? String(val) : '');
        const taskId = row.task_id ?? row.Task?.id;
        const commentId = row.id;
        if (taskId && commentId && text) {
          const to = isProjectMode
            ? `/task/taskdetails/${taskId}#comment-${commentId}`
            : `/task/taskdetails/${taskId}#comment-${commentId}`;
          return (
            <Link
              to={to}
              className="text-primary"
              style={{ textDecoration: 'none', wordBreak: 'break-word' }}
              onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
              onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
            >
              {text}
            </Link>
          );
        }
        return text || '';
      },
      cellStyle: { whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: '400px' },
    },
  ];

  return (
    <div className="task-daily-updates-list">
      <ToastContainer />
      <button
        type="button"
        className="back-button-link btn-link border-0 bg-transparent p-0"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          color: '#C42126',
          fontSize: '1rem',
          textDecoration: 'none',
          marginBottom: '1rem',
          cursor: 'pointer',
        }}
        onClick={() => navigate(-1)}
      >
        <FiArrowLeft style={{ marginRight: '0.5rem' }} />
        Back
      </button>

      <div className="card heightcard">
        <div className="card-header details-card-header text-start d-flex justify-content-between align-items-center">
          <h5 className="text-base sm:text-lg font-semibold mb-0">
            {entityTitle || (isProjectMode ? `Project #${projectId}` : `Task #${taskid}`)}
          </h5>
          {updates.length > 0 && (
            <button
              className="btn btn-outline-primary btn-sm"
              type="button"
              onClick={handleExportDailyUpdates}
              title="Download daily updates as Excel"
            >
              <i className="fa fa-download me-1"></i>
              Download
            </button>
          )}
        </div>
        <div className="card-body">
          {/* Project/entity details - above the table */}
          <div className="daily-updates-project-info mb-3 p-2" style={{ background: '#f8f9fa', borderRadius: '6px', fontSize: '0.95rem' }}>
            <div className="d-flex flex-wrap gap-3">
              {!isProjectMode && (
                <div>
                  <span style={{ color: '#6c757d', fontWeight: '600' }}>Project:</span>{' '}
                  {projectInfo.id ? (
                    <Link
                      to={`/task/projectdetails/${projectInfo.id}`}
                      className="text-primary"
                      style={{ textDecoration: 'none' }}
                      onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                    >
                      {projectInfo.name || 'N/A'}
                      <i className="fas fa-external-link-alt ms-1" style={{ fontSize: '0.75rem' }}></i>
                    </Link>
                  ) : (
                    <span>{projectInfo.name || 'N/A'}</span>
                  )}
                </div>
              )}
              <div>
                <span style={{ color: '#6c757d', fontWeight: '600' }}>Start:</span>{' '}
                <span>{projectInfo.start_date ? formatDateToDDMMMYYYY(projectInfo.start_date) : 'N/A'}</span>
              </div>
              <div>
                <span style={{ color: '#6c757d', fontWeight: '600' }}>End:</span>{' '}
                <span>{projectInfo.due_date ? formatDateToDDMMMYYYY(projectInfo.due_date) : 'N/A'}</span>
              </div>
            </div>
          </div>
          {loading && updates.length === 0 ? (
            <div className="text-center py-5 text-muted">Loading...</div>
          ) : (
            <>
              <CommonTable
                columns={columns}
                data={updates}
                emptyMessage={isProjectMode
                  ? "No daily updates for this project yet."
                  : "No daily updates for this task yet."}
                emptyStateIcon="fas fa-clipboard-list"
                getRowId={(row) => row.id}
                maxHeight="70vh"
                minHeight="300px"
              />
              {hasMore && updates.length > 0 && (
                <div className="text-center mt-3">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={loadMore}
                    disabled={loading}
                  >
                    {loading ? 'Loading...' : 'Load More'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskDailyUpdatesList;
